import { useCallback } from 'react';

import { ValidateProjectName } from '@/connection/projects/validate-project-name';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { saveWorkingProjectByFields, setCreateProjectErrorFields } from '@/store/slices/project';
import {
  selectProjectGlobalTemplateType,
  selectProjectIsAutoGeneratedName,
  selectProjectChannel,
  selectProjectName,
  selectProjectSettings,
  selectWorkingProjectHomepage,
  selectWorkingProjectNameTranslations,
  selectWorkingProjectRegistrationSettings,
  getWorkingProjectEventId,
} from '@/store/slices/project/selectors';
import { getActiveChannel } from '@/store/slices/user/selectors';
import { selectFeatureFlag } from '@/store/slices/feature-flags/selectors';
import { FeatureFlagsEnum } from '@/store/slices/feature-flags/types';
import { TSaveWorkingProjectByFields } from '@/store/slices/project/types';
import { PageModule, PageModuleType } from '@/types/module.types';
import { TranslateString } from '@/types/translation.types';
import { getDisplayProjectName, getStrippedProjectAutoNamePart } from '@/utils/get-project-name';
import { updateTranslateKey } from '@/utils/update-translation-key';
import toastTrigger from '@ui/toast/toast-trigger';
import { EToastTypes } from '@ui/toast/toast.types';

/**
 * Single source of truth for updating the project name and keeping it in sync
 * across the registration page, landing page, and home page banner titles.
 *
 * When any page title or the project name changes, call the returned handler.
 * It will:
 *   - Validate the name (auto-generated check, uniqueness check)
 *   - Dispatch field errors or a toast on failure
 *   - Update workingProject.name_translations for the given language
 *   - Update workingProject.name (for base language changes only)
 *   - Sync all main_event_banner module content.title fields
 *   - Sync workingProject.registration_settings.title
 *   - Sync the session title if syncSessionUuid is provided
 *   - Persist the changes via saveWorkingProjectByFields
 *
 * Backwards compatibility: if name_translations has never been set on a project,
 * the existing main_event_banner content.title (or registration_settings.title)
 * is used to initialize it.
 */
export function useUpdateProjectName(syncSessionUuid?: string) {
  const dispatch = useAppDispatch();
  const workingProjectName = useAppSelector(selectProjectName);
  const workingProjectSettings = useAppSelector(selectProjectSettings);
  const workingProjectChannel = useAppSelector(selectProjectChannel);
  const workingProjectGlobalTemplateType = useAppSelector(selectProjectGlobalTemplateType);
  const isAutoGeneratedName = useAppSelector(selectProjectIsAutoGeneratedName);
  const userActiveChannel = useAppSelector(getActiveChannel);
  const workingProjectEventId = useAppSelector(getWorkingProjectEventId);
  const homepage = useAppSelector(selectWorkingProjectHomepage);
  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);
  const nameTranslations = useAppSelector(selectWorkingProjectNameTranslations);
  const projectTitleSyncEnabled = useAppSelector(selectFeatureFlag(FeatureFlagsEnum.project_title_sync));

  return useCallback(
    async (name: string, language: string, baseLanguage: string) => {
      if (!userActiveChannel) return;

      // --- Validation ---

      if (isAutoGeneratedName) {
        const strippedName = getStrippedProjectAutoNamePart({
          channel: userActiveChannel,
          globalTemplateType: workingProjectGlobalTemplateType,
          projectName: workingProjectName,
          isAutoGeneratedName: true,
        });

        if (`${name} ${strippedName}` === workingProjectName) {
          dispatch(setCreateProjectErrorFields(null));
          return;
        }

        const displayProjectName = getDisplayProjectName({
          channel: workingProjectChannel,
          isAutoGeneratedName,
          globalTemplateType: workingProjectGlobalTemplateType,
          projectName: workingProjectName,
        });

        if (name === displayProjectName) {
          dispatch(setCreateProjectErrorFields({ projectName: 'Give your project a name' }));
          return;
        }
      } else {
        if (name === workingProjectName) {
          dispatch(setCreateProjectErrorFields(null));
          return;
        }
      }

      dispatch(setCreateProjectErrorFields(null));

      if (!name.length) {
        dispatch(setCreateProjectErrorFields({ projectName: 'Project name required' }));
        return;
      }

      try {
        const nameExists = await ValidateProjectName({
          projectName: name,
          channel: userActiveChannel,
          eventId: workingProjectEventId ?? 0,
        });
        if (nameExists.exists) {
          dispatch(setCreateProjectErrorFields({ projectName: 'Project name already exists' }));
          return;
        }
      } catch (error) {
        console.error('Error validating project name', error);
        toastTrigger('There was an error validating the project name.', {
          description: 'Please refresh and try again.',
          type: EToastTypes.error,
          autoClose: 10000,
        });
        return;
      }

      // --- Sync & Save ---

      // Initialize from existing name_translations, fall back to the banner
      // module's content.title, then registration_settings.title, then project.name
      // for projects that predate this feature.
      const existingTranslations: TranslateString = nameTranslations ??
        homepage?.modules?.find((m: PageModule) => m.type === PageModuleType.main_event_banner)?.content?.title ??
        registrationSettings?.title ?? { base: workingProjectName ?? '', changed: '' };

      const updatedNameTranslations = updateTranslateKey({
        translateString: existingTranslations,
        input: name,
        language,
        baseLanguage,
      });

      const syncBannerTitle = (modules?: PageModule[]): PageModule[] => {
        return (modules || []).map((m: PageModule) => {
          if (m.type !== PageModuleType.main_event_banner) {
            return m;
          }
          return { ...m, content: { ...m.content, title: updatedNameTranslations } };
        });
      };

      const updatedFields: TSaveWorkingProjectByFields = {
        name_translations: updatedNameTranslations,
      };

      // Only update the plain-string name when editing the base language,
      // to preserve the existing contract that project.name is untranslated.
      if (language === baseLanguage) {
        updatedFields.name = name.trim();
        if (workingProjectSettings) {
          updatedFields.settings = { ...workingProjectSettings, is_auto_generated_name: false };
        }
      }

      if (projectTitleSyncEnabled && registrationSettings) {
        updatedFields.registration_settings = { ...registrationSettings, title: updatedNameTranslations };
      }

      if (projectTitleSyncEnabled && homepage) {
        updatedFields.homepage = {
          ...homepage,
          modules: syncBannerTitle(homepage?.modules),
          post_register_home_modules: syncBannerTitle(homepage?.post_register_home_modules),
        };
      }

      if (syncSessionUuid) {
        updatedFields.sessionsToSave = [{ uuid: syncSessionUuid, title: updatedNameTranslations }];
      }

      dispatch(saveWorkingProjectByFields(updatedFields));
    },
    [
      dispatch,
      workingProjectName,
      workingProjectSettings,
      workingProjectChannel,
      workingProjectGlobalTemplateType,
      isAutoGeneratedName,
      userActiveChannel,
      workingProjectEventId,
      homepage,
      registrationSettings,
      nameTranslations,
      projectTitleSyncEnabled,
      syncSessionUuid,
    ]
  );
}
